import {
    escapeHTML,
    formatTsunamiDateTime,
    getTsunamiMarkerColor,
    getTsunamiMarkerFillColor,
    getTsunamiWarningBorderColor,
    getTsunamiWarningTypeLabel,
    getTsunamiWarningTypeRank,
    mergeTsunamiStationsByLatestObservation
} from '../helpers/helpers-tsunami';
import Panel from './panel';

const TSUNAMI_LEGEND_ITEMS = [
    {level: 0, label: '情報のみ'},
    {level: 1, label: '0.2m未満'},
    {level: 2, label: '0.2m以上 1.0m未満'},
    {level: 3, label: '1.0m以上 3.0m未満'},
    {level: 4, label: '3.0m以上'}
];
const TSUNAMI_WARNING_LEGEND_ITEMS = [
    {rank: 0, label: '注意報・警報なし'},
    {rank: 1, label: '津波注意報'},
    {rank: 2, label: '津波警報'},
    {rank: 3, label: '大津波警報'}
];
const SELECT_ALL = '__all__';

function buildHeaderHtml() {
    return [
        '<div class="tsunami-panel-header">',
        '<span class="tsunami-panel-title-text">津波情報</span>',
        '<span class="tsunami-panel-caption">過去7日間の津波の情報が表示できます</span>',
        '</div>'
    ].join('');
}

function buildTsunamiLegendHtml() {
    return [
        '<div class="tsunami-legend-section">',
        '<div class="tsunami-legend-title">波高と色の凡例</div>',
        '<div class="tsunami-legend-list">',
        ...TSUNAMI_LEGEND_ITEMS.map(({level, label}) => [
            '<div class="tsunami-legend-row">',
            `<span class="tsunami-legend-swatch" style="background-color: ${getTsunamiMarkerFillColor(level)};"></span>`,
            `<span class="tsunami-legend-label">${escapeHTML(label)}</span>`,
            '</div>'
        ].join('')),
        '</div>',
        '</div>',
        '<div class="tsunami-legend-section">',
        '<div class="tsunami-legend-title">円周色（注意報・警報）</div>',
        '<div class="tsunami-legend-list">',
        ...TSUNAMI_WARNING_LEGEND_ITEMS.map(({rank, label}) => [
            '<div class="tsunami-legend-row">',
            `<span class="tsunami-legend-dot" style="background-color: ${getTsunamiMarkerColor(0)}; border-color: ${getTsunamiWarningBorderColor(rank)};"></span>`,
            `<span class="tsunami-legend-label">${escapeHTML(label)}</span>`,
            '</div>'
        ].join('')),
        '</div>',
        '</div>'
    ].join('');
}

function buildSummaryRow(label, value, className = '') {
    return [
        `<div class="tsunami-summary-row ${className}">`,
        `<div class="tsunami-summary-label">${escapeHTML(label)}</div>`,
        `<div class="tsunami-summary-value">${value || '-'}</div>`,
        '</div>'
    ].join('');
}

function buildStationRows(stations, selectedStationCode) {
    if (!stations.length) {
        return '<div class="tsunami-empty">表示できる観測所データがありません。</div>';
    }

    return stations.map(station => {
        const isSelected = selectedStationCode && station.stationcode === selectedStationCode,
            mapStatus = station.hasCoordinate ? '' : '<span class="tsunami-station-note">地図表示なし</span>';

        return [
            `<div class="tsunami-station-row${isSelected ? ' tsunami-station-row-selected' : ''}" data-stationcode="${escapeHTML(station.stationcode)}">`,
            `<div class="tsunami-station-level" style="background-color: ${getTsunamiMarkerFillColor(station.markerLevel)}; border-color: ${getTsunamiWarningBorderColor(station.warningRank)};"></div>`,
            '<div class="tsunami-station-text">',
            `<div class="tsunami-station-name">${escapeHTML(station.stationname || '-')}</div>`,
            `<div class="tsunami-station-meta">波高: ${escapeHTML(station.height_desc || '-')} / 観測: ${escapeHTML(formatTsunamiDateTime(station.observetime) || '-')} / 到達: ${escapeHTML(formatTsunamiDateTime(station.arrivaltime) || '-')}</div>`,
            `<div class="tsunami-station-area">${escapeHTML(station.areaname || '-')} / ${escapeHTML(station.wavetypename || '-')} ${mapStatus}</div>`,
            '</div>',
            '</div>'
        ].join('');
    }).join('');
}

function buildCauseEarthquakeHtml(causeEarthquake) {
    if (!causeEarthquake || (!causeEarthquake.hypocentername && !causeEarthquake.magnitude)) {
        return '';
    }

    return [
        '<div class="tsunami-section-title">津波のもとになった地震</div>',
        '<div class="tsunami-summary-grid">',
        buildSummaryRow('震源地', escapeHTML(causeEarthquake.hypocentername || '-')),
        buildSummaryRow('マグニチュード', escapeHTML(causeEarthquake.magnitude || '-')),
        '</div>'
    ].join('');
}

function buildTsunamiEventRows(tsunamis) {
    if (!tsunamis.length) {
        return '<div class="tsunami-empty">津波情報はありません。</div>';
    }

    return tsunamis.map(tsunami => {
        const stations = Array.isArray(tsunami.stations) ? tsunami.stations : [],
            highestStation = stations.reduce((bestStation, station) => {
                if (!bestStation) {
                    return station;
                }

                const bestHeight = Number.isFinite(bestStation.heightValue) ? bestStation.heightValue : -1,
                    currentHeight = Number.isFinite(station.heightValue) ? station.heightValue : -1;

                if (currentHeight !== bestHeight) {
                    return currentHeight > bestHeight ? station : bestStation;
                }

                const bestObserved = bestStation.observetime ? new Date(bestStation.observetime).getTime() : NaN,
                    currentObserved = station.observetime ? new Date(station.observetime).getTime() : NaN;

                if (Number.isFinite(currentObserved) && Number.isFinite(bestObserved) && currentObserved !== bestObserved) {
                    return currentObserved > bestObserved ? station : bestStation;
                }
                if (Number.isFinite(currentObserved) && !Number.isFinite(bestObserved)) {
                    return station;
                }
                return bestStation;
            }, null),
            highestAreaName = [
                highestStation?.areaname,
                ...stations.map(station => station.areaname).filter(Boolean),
                highestStation?.stationname,
                ...stations.map(station => station.stationname).filter(Boolean),
                tsunami.title,
                tsunami.headline,
                '地域不明'
            ].find(Boolean);

        return [
            `<div class="tsunami-event-row" data-eventid="${escapeHTML(tsunami.eventid)}" role="button" tabindex="0">`,
            '<div class="tsunami-station-text">',
            `<div class="tsunami-station-name">${escapeHTML(tsunami.warningType || '津波情報')}</div>`,
            `<div class="tsunami-station-meta">${escapeHTML(formatTsunamiDateTime(tsunami.report_datetime) || '-')} / ${escapeHTML(`${tsunami.targetAreaCount}地域`)}</div>`,
            `<div class="tsunami-station-area">最大観測値: ${escapeHTML(tsunami.maxHeightDisplay || '-')} / ${escapeHTML(highestAreaName || '地域不明')}</div>`,
            '</div>',
            '</div>'
        ].join('');
    }).join('');
}

function buildTsunamiPanelHtml(tsunami, selectedStationCode) {
    return [
        '<div class="tsunami-panel-body">',
        '<div class="tsunami-event-switcher">',
        '<select class="tsunami-event-select" aria-label="津波情報の選択"></select>',
        '</div>',
        '<div class="tsunami-summary-grid">',
        buildSummaryRow('発表時刻', escapeHTML(formatTsunamiDateTime(tsunami.report_datetime) || '-')),
        buildSummaryRow('警報種別', escapeHTML(tsunami.warningType || '-')),
        buildSummaryRow('対象地域数', escapeHTML(`${tsunami.targetAreaCount}地域`)),
        buildSummaryRow('最大観測値', escapeHTML(tsunami.maxHeightDisplay || '-'), 'tsunami-summary-row-highlight'),
        '</div>',
        buildCauseEarthquakeHtml(tsunami.causeEarthquake),
        '<div class="tsunami-section-title">観測所一覧</div>',
        `<div class="tsunami-station-list">${buildStationRows(tsunami.stations || [], selectedStationCode)}</div>`,
        tsunami.hiddenStationCount > 0 ?
            `<div class="tsunami-panel-note">座標未登録の観測所 ${tsunami.hiddenStationCount} 件は地図上に表示していません。</div>` :
            '',
        '</div>'
    ].join('');
}

function buildAllTsunamiPanelHtml(tsunamis) {
    const mergedStations = mergeTsunamiStationsByLatestObservation(
            tsunamis.flatMap(tsunami => tsunami.stations || [])
        ),
        latestTsunami = tsunamis[0] || null,
        strongestStation = mergedStations.reduce((bestStation, station) => {
            if (!bestStation) {
                return station;
            }

            const stationRank = Math.max(
                    getTsunamiWarningTypeRank(station.wavetypename),
                    Number(station.markerLevel) || 0
                ),
                bestRank = Math.max(
                    getTsunamiWarningTypeRank(bestStation.wavetypename),
                    Number(bestStation.markerLevel) || 0
                );

            if (stationRank !== bestRank) {
                return stationRank > bestRank ? station : bestStation;
            }
            return (Number(station.heightValue) || -1) > (Number(bestStation.heightValue) || -1) ?
                station :
                bestStation;
        }, null),
        maxHeightStation = mergedStations.reduce((bestStation, station) => {
            if (!bestStation) {
                return station;
            }
            return (Number(station.heightValue) || -1) > (Number(bestStation.heightValue) || -1) ?
                station :
                bestStation;
        }, null),
        hiddenStationCount = mergedStations.filter(station => !station.hasCoordinate).length;

    return [
        '<div class="tsunami-panel-body">',
        '<div class="tsunami-event-switcher">',
        '<select class="tsunami-event-select" aria-label="津波情報の選択"></select>',
        '</div>',
        '<div class="tsunami-summary-grid">',
        buildSummaryRow('件数', escapeHTML(`${tsunamis.length}件`)),
        buildSummaryRow('最新発表時刻', escapeHTML(formatTsunamiDateTime(latestTsunami?.report_datetime) || '-')),
        buildSummaryRow(
            '最大警報',
            escapeHTML(
                strongestStation ?
                    getTsunamiWarningTypeLabel(strongestStation.wavetypename, strongestStation.markerLevel) :
                    '-'
            )
        ),
        buildSummaryRow(
            '観測所数 / 最大観測値',
            escapeHTML(`${mergedStations.length}件 / ${maxHeightStation?.height_desc || '-'}`),
            'tsunami-summary-row-highlight'
        ),
        '</div>',
        '<div class="tsunami-section-title">津波一覧</div>',
        `<div class="tsunami-station-list">${buildTsunamiEventRows(tsunamis)}</div>`,
        hiddenStationCount > 0 ?
            `<div class="tsunami-panel-note">座標未登録の観測所 ${hiddenStationCount} 件は地図上に表示していません。</div>` :
            '',
        '</div>'
    ].join('');
}

function buildTsunamiOptionLabel(tsunami) {
    const reportTime = formatTsunamiDateTime(tsunami.report_datetime || '', false),
        warningType = tsunami.warningType || '津波情報';

    return [
        reportTime || '時刻不明',
        warningType,
        Number.isFinite(tsunami.targetAreaCount) ? `${tsunami.targetAreaCount}地域` : ''
    ].filter(Boolean).join(' ');
}

function bindSelectableRows(container, selector, onSelect) {
    const rows = container ? Array.from(container.querySelectorAll(selector)) : [];

    rows.forEach(row => {
        const triggerSelection = () => {
            const eventId = row.dataset.eventid;

            if (eventId) {
                onSelect(eventId);
            }
        };

        row.addEventListener('click', event => {
            event.stopPropagation();
            triggerSelection();
        });
        row.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                triggerSelection();
            }
        });
    });
}

export default class extends Panel {

    constructor(options) {
        super(Object.assign({
            className: 'tsunami-panel',
            tsunamis: [],
            selectedEventId: '',
            selectedStationCode: '',
            headerClickAction: 'close'
        }, options));
    }

    addTo(map) {
        const me = this;

        me._closing = false;
        super.addTo(map)
            .setTitle(buildHeaderHtml())
            .updateContent();
        me.renderLegend();

        me._container.querySelector('#panel-button').addEventListener('click', event => {
            event.stopPropagation();
            me.remove();
        });

        return me;
    }

    setTsunamis(tsunamis, selectedEventId = this._options.selectedEventId) {
        this._options.tsunamis = Array.isArray(tsunamis) ? tsunamis : [];
        this._options.selectedEventId = selectedEventId || '';

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    setSelectedEventId(selectedEventId) {
        this._options.selectedEventId = selectedEventId || '';
        if (selectedEventId === SELECT_ALL) {
            this._options.selectedStationCode = '';
        }

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    setSelectedStationCode(selectedStationCode) {
        this._options.selectedStationCode = selectedStationCode || '';

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    isAllSelected() {
        return this._options.selectedEventId === SELECT_ALL;
    }

    getSelectedTsunami() {
        const tsunamis = this._options.tsunamis || [],
            selectedEventId = this._options.selectedEventId;

        if (selectedEventId === SELECT_ALL) {
            return null;
        }
        return tsunamis.find(tsunami => tsunami.eventid === selectedEventId) || tsunamis[0] || null;
    }

    updateEventOptions() {
        const me = this,
            select = me._eventSelect,
            tsunamis = me._options.tsunamis || [],
            selectedTsunami = me.getSelectedTsunami(),
            selectedValue = me.isAllSelected() ?
                SELECT_ALL :
                (selectedTsunami ? selectedTsunami.eventid : tsunamis[0]?.eventid || '');

        if (!select) {
            return me;
        }

        select.innerHTML = '';

        if (!tsunamis.length) {
            const option = document.createElement('option');

            option.value = '';
            option.textContent = '津波情報はありません';
            select.appendChild(option);
            select.disabled = true;
            return me;
        }

        const allOption = document.createElement('option');

        allOption.value = SELECT_ALL;
        allOption.textContent = 'すべて';
        select.appendChild(allOption);

        tsunamis.forEach(tsunami => {
            const option = document.createElement('option');

            option.value = tsunami.eventid;
            option.textContent = buildTsunamiOptionLabel(tsunami);
            select.appendChild(option);
        });
        select.disabled = false;
        select.value = selectedValue;

        return me;
    }

    bindEventSelect() {
        const me = this,
            select = me._container && me._container.querySelector('.tsunami-event-select');

        if (!select) {
            me._eventSelect = null;
            return me;
        }
        if (select === me._eventSelect) {
            return me;
        }

        me._eventSelect = select;
        ['mousedown', 'click', 'touchstart'].forEach(eventName => {
            select.addEventListener(eventName, event => {
                event.stopPropagation();
            });
        });
        select.addEventListener('change', event => {
            const onSelectTsunami = me._options.onSelectTsunami;

            me.setSelectedEventId(event.target.value);
            if (onSelectTsunami) {
                onSelectTsunami(event.target.value, '');
            }
        });

        return me;
    }

    bindEventRows() {
        const me = this,
            onSelectTsunami = me._options.onSelectTsunami;

        if (!onSelectTsunami || !me._container) {
            return me;
        }

        bindSelectableRows(me._container, '.tsunami-event-row[data-eventid]', eventId => {
            me.setSelectedEventId(eventId);
            onSelectTsunami(eventId, '');
        });

        return me;
    }

    highlightSelectedStationRow() {
        const row = this._container && this._container.querySelector('.tsunami-station-row-selected');

        if (row && typeof row.scrollIntoView === 'function') {
            row.scrollIntoView({
                block: 'nearest'
            });
        }
        return this;
    }

    renderLegend() {
        const me = this;

        if (!me._container) {
            return me;
        }
        if (!me._legendElement) {
            me._legendElement = document.createElement('div');
            me._legendElement.className = 'tsunami-legend';
            me._container.appendChild(me._legendElement);
        }

        me._legendElement.innerHTML = buildTsunamiLegendHtml();
        return me;
    }

    removeLegend() {
        const me = this,
            legendElement = me._legendElement;

        if (legendElement && legendElement.parentNode) {
            legendElement.parentNode.removeChild(legendElement);
        }
        me._legendElement = null;
        return me;
    }

    updateContent() {
        const tsunamis = this._options.tsunamis || [],
            tsunami = this.getSelectedTsunami();

        this.setTitle(buildHeaderHtml());

        if (!tsunamis.length) {
            this.setHTML('<div class="tsunami-empty">津波情報はありません。</div>');
            this._eventSelect = null;
            return this;
        }

        this.setHTML(
            this.isAllSelected() ?
                buildAllTsunamiPanelHtml(tsunamis) :
                buildTsunamiPanelHtml(tsunami, this._options.selectedStationCode)
        );
        this.bindEventSelect();
        this.updateEventOptions();
        this.bindEventRows();
        if (!this.isAllSelected()) {
            this.highlightSelectedStationRow();
        }
        return this;
    }

    isClosing() {
        return !!this._closing;
    }

    remove() {
        const me = this,
            onClose = me._options.onClose;

        if (!me.isOpen()) {
            return me;
        }

        me._closing = true;
        me.removeLegend();
        super.remove();

        if (onClose) {
            onClose();
        }
        setTimeout(() => {
            me._closing = false;
        }, 300);
        return me;
    }

}
